/**
 * Simple client-side A/B testing hook.
 * Assigns a variant on first visit and persists it in localStorage.
 */

import { useState, useEffect } from 'react'
import { trackEvent } from '../utils/analytics'

// Define available tests and their variants
export const AB_TESTS = {
  hero_headline: {
    variants: ['control', 'value_finder', 'data_driven'] as const,
    // Map variant IDs to actual content
    content: {
      control: {
        line1: 'Bet Smarter,',
        line2: 'Not Harder',
      },
      value_finder: {
        line1: 'Find Value Bets',
        line2: 'in Seconds',
      },
      data_driven: {
        line1: 'Data-Driven',
        line2: 'Football Betting',
      },
    },
  },
} as const

type TestName = keyof typeof AB_TESTS
type VariantOf<T extends TestName> = (typeof AB_TESTS)[T]['variants'][number]

/**
 * Hook to get the assigned variant for an A/B test.
 * Assigns a random variant on first visit and persists in localStorage.
 */
export function useABTest<T extends TestName>(testName: T): VariantOf<T> {
  const [variant, setVariant] = useState<VariantOf<T>>(() => {
    // Check if running in browser
    if (typeof window === 'undefined') {
      return AB_TESTS[testName].variants[0] as VariantOf<T>
    }

    const key = `ab_test_${testName}`
    const stored = localStorage.getItem(key)

    if (stored && AB_TESTS[testName].variants.includes(stored as any)) {
      return stored as VariantOf<T>
    }

    // Assign random variant
    const variants = AB_TESTS[testName].variants
    const randomVariant = variants[Math.floor(Math.random() * variants.length)]
    localStorage.setItem(key, randomVariant)

    return randomVariant as VariantOf<T>
  })

  // Track variant assignment once
  useEffect(() => {
    const key = `ab_test_${testName}_tracked`
    if (!sessionStorage.getItem(key)) {
      trackEvent('ab_test_assigned', {
        test_name: testName,
        variant,
      })
      sessionStorage.setItem(key, 'true')
    }
  }, [testName, variant])

  return variant
}

/**
 * Get the content for a hero headline variant.
 */
export function getHeadlineContent(variant: VariantOf<'hero_headline'>) {
  return AB_TESTS.hero_headline.content[variant]
}
